import {
  PlannerConfigEntity,
  PlannerConfigEntityWithOptionalFields,
} from '@/services/configurator/PlannerConfigTypes';
import { PlannerEnv, envToBaseUrl } from './env';
import Fetch from '@/services/fetch/Fetch';
import {
  findCollidingCategories,
  CollidingCategory,
} from './findCollidingCategories';

export type { CollidingCategory } from './findCollidingCategories';

export type PlannerSaveResult = {
  savedToState: string;
  stagingId: string;
};

export class ConfiguratorApi {
  private static _instance: ConfiguratorApi;

  static getInstance() {
    if (!ConfiguratorApi._instance) {
      ConfiguratorApi._instance = new ConfiguratorApi();
    }
    return ConfiguratorApi._instance;
  }

  async getDraftPlanner(
    env: PlannerEnv,
    plannerId: string,
  ): Promise<PlannerConfigEntityWithOptionalFields> {
    const url = `${this.baseUrl(env)}/staging/data/${plannerId}`;
    const response = await Fetch.json(url);
    return response as PlannerConfigEntityWithOptionalFields;
  }

  async getPlannerIdentifiers(env: PlannerEnv): Promise<string[]> {
    const url = `${this.baseUrl(env)}/published/listPlannerIds`;
    const response: { ids: string[] } = await Fetch.json(url);
    return response.ids;
  }

  async getPublishedPlanner(
    env: PlannerEnv,
    plannerId: string,
  ): Promise<PlannerConfigEntityWithOptionalFields> {
    const url = `${this.baseUrl(env)}/published/data/${plannerId}`;
    const response = await Fetch.json(url);
    return response as PlannerConfigEntityWithOptionalFields;
  }

  async listPublisheddPlanners(
    env: PlannerEnv,
    options: { offset: number; limit: number; identifier?: string },
  ): Promise<Array<PlannerConfigEntityWithOptionalFields>> {
    let url = `${this.baseUrl(env)}/published/list`;

    options.offset = options.offset ?? 0;
    options.limit = options.limit ?? 0;

    url += `?offset=${options.offset}`;
    url += `&limit=${options.limit}`;
    if (options.identifier) {
      url += `&identifier=${options.identifier}`;
    }

    const result = await Fetch.json(url);

    //@ts-expect-error - response.data is not defined in Fetch.json
    const response = result.data as PlannerConfigEntityWithOptionalFields[];

    return response;
  }

  private collidingCategoriesCache: Map<
    PlannerEnv,
    { data: PlannerConfigEntityWithOptionalFields[]; timestamp: number }
  > = new Map();
  private static CACHE_TTL_MS = 60_000;

  async getCollidingCategories(
    env: PlannerEnv,
    plannerId: string,
    plannerActiveCategories: string[],
    plannerActiveLocales: string[],
  ): Promise<CollidingCategory[]> {
    if (!plannerActiveCategories.length || !plannerActiveLocales.length) {
      return [];
    }
    let allPlanners: PlannerConfigEntityWithOptionalFields[];
    const cached = this.collidingCategoriesCache.get(env);

    if (
      cached &&
      Date.now() - cached.timestamp < ConfiguratorApi.CACHE_TTL_MS
    ) {
      allPlanners = cached.data;
    } else {
      const url = `${this.baseUrl(env)}/published/list?offset=0&limit=1000`;
      const result = await Fetch.json(url);

      //@ts-expect-error - response.data is not defined in Fetch.json
      allPlanners = result.data as PlannerConfigEntityWithOptionalFields[];

      this.collidingCategoriesCache.set(env, {
        data: allPlanners,
        timestamp: Date.now(),
      });
    }

    return findCollidingCategories(
      plannerId,
      plannerActiveCategories,
      plannerActiveLocales,
      allPlanners,
    );
  }

  async listDraftPlanners(
    env: PlannerEnv,
    options: { identifier?: string; userName?: string },
  ): Promise<Array<PlannerConfigEntityWithOptionalFields>> {
    const url = new URL(
      `${this.baseUrl(env)}/staging/list`,
      window.location.origin,
    );

    if (options.identifier) {
      url.searchParams.set('identifier', options.identifier);
    }

    const result = await Fetch.json(url.toString());
    //@ts-expect-error - response.data is not defined in Fetch.json

    const response = result.data as PlannerConfigEntityWithOptionalFields[];
    if (options.userName) {
      return response.filter((it) => it.modifiedBy === options.userName);
    }
    return response;
  }

  /**
   * Fetches translations for a given planner identifier, country code and language code
   */
  async getTranslation(
    identifier: string,
    env: PlannerEnv,
    countryCode: string,
    languageCode: string,
  ): Promise<Record<string, string>> {
    const url = `${this.baseUrl(env)}/translations/${identifier}/?countryCode=${countryCode}&languageCode=${languageCode}`;
    const result: { translations: Record<string, string> } =
      await Fetch.json(url);
    return result.translations;
  }

  /**
   * Updates data for a given planner id in Staging collection
   */
  async updateDraft(updateData: Partial<PlannerConfigEntity>, id: string) {
    if (id) {
      const url = `${this.baseUrl('cte')}/staging/update/${id}`;
      await Fetch.json(url, {
        method: 'POST',
        body: JSON.stringify(updateData),
      }).catch((error: Error) => {
        if (error.message && !error.message?.includes('status 400')) {
          throw error;
        }
      });
    } else {
      throw new Error(`Invalid update data: ${JSON.stringify(updateData)}`);
    }
  }

  /**
   * Adds a new planner in the Staging collection of type PlannerConfigEntity
   */
  public async createDraft(
    newPlanner: PlannerConfigEntity,
  ): Promise<PlannerSaveResult> {
    if (newPlanner) {
      const url = `${this.baseUrl('cte')}/staging/new`;
      return Fetch.json(url, {
        method: 'POST',
        body: JSON.stringify(newPlanner),
      });
    } else {
      throw new Error(`Invalid planner data: ${JSON.stringify(newPlanner)}`);
    }
  }

  private baseUrl(env: PlannerEnv): string {
    return envToBaseUrl(env);
  }
}
